import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface FeedbackAnalyticsProps {
  data: {
    summary: {
      totalFeedback: number;
      helpfulCount: number;
      unhelpfulCount: number;
      neutralCount: number;
      helpfulRate: number;
      unhelpfulRate: number;
      feedbackRate: number;
      totalMessages: number;
    };
    distribution: Array<{ type: string; count: number }>;
    trends: Array<{ date: string; feedbackType: string; count: number }>;
    topMessages: Array<{
      messageId: string;
      feedbackCount: number;
      helpfulCount: number;
      unhelpfulCount: number;
      content: string;
    }>;
    topConversations: Array<{
      conversationId: string;
      feedbackCount: number;
      helpfulCount: number;
      title: string;
    }>;
    period: {
      days: number;
      startDate: string;
      endDate: string;
    };
  };
}

export const FeedbackAnalytics = ({ data }: FeedbackAnalyticsProps) => {
  const { summary, trends, topMessages, topConversations } = data;

  // Prepare line chart data - group by date
  const trendsByDate = trends.reduce((acc, item) => {
    const date = new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!acc[date]) {
      acc[date] = { date, helpful: 0, unhelpful: 0, neutral: 0 };
    }
    acc[date][item.feedbackType as 'helpful' | 'unhelpful' | 'neutral'] = item.count;
    return acc;
  }, {} as Record<string, { date: string; helpful: number; unhelpful: number; neutral: number }>);

  const lineChartData = Object.values(trendsByDate);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Feedback</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{summary.totalFeedback.toLocaleString()}</p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Helpful Rate</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{summary.helpfulRate.toFixed(1)}%</p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
              </svg>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">{summary.helpfulCount} helpful votes</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Unhelpful Rate</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{summary.unhelpfulRate.toFixed(1)}%</p>
            </div>
            <div className="bg-red-100 rounded-full p-3">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
              </svg>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">{summary.unhelpfulCount} unhelpful votes</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Feedback Rate</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">{summary.feedbackRate.toFixed(1)}%</p>
            </div>
            <div className="bg-purple-100 rounded-full p-3">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">{summary.totalFeedback} of {summary.totalMessages} messages</p>
        </div>
      </div>

      {/* Line Chart - Feedback Trends */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Feedback Trends Over Time</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={lineChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="helpful" stroke="#10b981" strokeWidth={2} name="Helpful" />
            <Line type="monotone" dataKey="unhelpful" stroke="#ef4444" strokeWidth={2} name="Unhelpful" />
            <Line type="monotone" dataKey="neutral" stroke="#8b5cf6" strokeWidth={2} name="Neutral" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Messages */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Messages by Feedback</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">👍</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">👎</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {topMessages.slice(0, 10).map((message, index) => (
                  <tr key={message.messageId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="max-w-xs truncate" title={message.content}>
                        {message.content}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{message.feedbackCount}</td>
                    <td className="px-4 py-3 text-sm text-green-600">{message.helpfulCount}</td>
                    <td className="px-4 py-3 text-sm text-red-600">{message.unhelpfulCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Conversations */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Conversations by Feedback</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversation</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">👍</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {topConversations.slice(0, 10).map((conversation, index) => (
                  <tr key={conversation.conversationId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="max-w-xs truncate" title={conversation.title}>
                        {conversation.title}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{conversation.feedbackCount}</td>
                    <td className="px-4 py-3 text-sm text-green-600">{conversation.helpfulCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
